import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  InputBase,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
  alpha,
  useTheme,
  Chip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Search as SearchIcon,
  Notifications as NotificationsIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Help as HelpIcon,
  AssignmentInd as AssignmentIcon,
  Schedule as ScheduleIcon,
  LocalHospital as HospitalIcon,
  Payment as PaymentIcon,
  Campaign as CampaignIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../hooks/useNotifications';
import { HospitalNotification, HospitalNotificationType } from '../../types/hospital-notification';

interface TopBarProps {
  onSidebarOpen: () => void;
  drawerWidth?: number;
}

const TopBar: React.FC<TopBarProps> = ({ onSidebarOpen, drawerWidth = 240 }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notificationsAnchor, setNotificationsAnchor] = useState<null | HTMLElement>(null);

  // NotificationContext와 연동
  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    isLoading,
    error
  } = useNotifications();

  // Use type assertion to bypass type checking
  // @ts-ignore
  const userData = user as any;

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationsOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationsAnchor(event.currentTarget);
  };

  const handleNotificationsClose = () => {
    setNotificationsAnchor(null);
  };

  const handleLogout = async () => {
    try {
      handleMenuClose();
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('로그아웃 중 오류가 발생했습니다', error);
    }
  };

  const handleProfileClick = () => {
    handleMenuClose();
    navigate('/profile');
  };

  // 알림 타입별 아이콘 반환
  const getNotificationIcon = (type: HospitalNotificationType) => {
    switch (type) {
      case HospitalNotificationType.NURSE_APPLICATION_RECEIVED:
        return <AssignmentIcon fontSize="small" sx={{ mr: 1, color: 'info.main' }} />;
      case HospitalNotificationType.INTERVIEW_SCHEDULED:
      case HospitalNotificationType.INTERVIEW_REMINDER:
        return <ScheduleIcon fontSize="small" sx={{ mr: 1, color: 'warning.main' }} />;
      case HospitalNotificationType.HIRING_CONFIRMED:
      case HospitalNotificationType.NURSE_HIRED:
        return <CheckCircleIcon fontSize="small" sx={{ mr: 1, color: 'success.main' }} />;
      case HospitalNotificationType.SHIFT_ASSIGNED:
      case HospitalNotificationType.SHIFT_COVERAGE_REQUEST:
        return <HospitalIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />;
      case HospitalNotificationType.PAYMENT_COMPLETED:
        return <PaymentIcon fontSize="small" sx={{ mr: 1, color: 'success.main' }} />;
      case HospitalNotificationType.SHIFT_CANCELLED:
        return <CancelIcon fontSize="small" sx={{ mr: 1, color: 'error.main' }} />;
      case HospitalNotificationType.URGENT_STAFF_NEEDED:
        return <CampaignIcon fontSize="small" sx={{ mr: 1, color: 'error.main' }} />;
      default:
        return <NotificationsIcon fontSize="small" sx={{ mr: 1, color: 'grey.500' }} />;
    }
  };

  // 알림 클릭 시 라우팅 처리
  const handleNotificationClick = (notification: HospitalNotification) => {
    // 알림을 읽음 처리
    if (!notification.isRead) {
      markAsRead(notification.id);
    }

    // 안전한 타입 변환으로 라우팅 처리
    const notificationType = notification.type as unknown as HospitalNotificationType;
    
    switch (notificationType) {
      case HospitalNotificationType.NURSE_APPLICATION_RECEIVED:
        navigate('/applications');
        break;
      case HospitalNotificationType.INTERVIEW_SCHEDULED:
      case HospitalNotificationType.INTERVIEW_REMINDER:
        navigate('/interviews');
        break;
      case HospitalNotificationType.SHIFT_ASSIGNED:
      case HospitalNotificationType.SHIFT_CANCELLED:
      case HospitalNotificationType.URGENT_STAFF_NEEDED:
      case HospitalNotificationType.SHIFT_COVERAGE_REQUEST:
        navigate('/shifts');
        break;
      case HospitalNotificationType.PAYMENT_COMPLETED:
        navigate('/payments');
        break;
      default:
        navigate('/dashboard');
    }

    handleNotificationsClose();
  };

  // 모두 읽음 처리
  const handleMarkAllAsRead = () => {
    markAllAsRead();
  };

  // 모든 알림 보기 페이지로 이동
  const handleViewAllNotifications = () => {
    navigate('/notifications');
    handleNotificationsClose();
  };

  // 시간 포맷팅
  const formatTimeAgo = (date: Date | string) => {
    const notificationDate = typeof date === 'string' ? new Date(date) : date;
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - notificationDate.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return '방금 전';
    if (diffInMinutes < 60) return `${diffInMinutes}분 전`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}시간 전`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}일 전`;
    
    return notificationDate.toLocaleDateString();
  };

  // 최근 5개 알림만 표시
  const recentNotifications = notifications.slice(0, 5);

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        width: { md: `calc(100% - ${drawerWidth}px)` },
        ml: { md: `${drawerWidth}px` },
        bgcolor: 'background.paper',
        color: 'text.primary',
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Toolbar sx={{ height: 64 }}>
        <IconButton
          color="inherit"
          edge="start"
          onClick={onSidebarOpen}
          sx={{ mr: 2, display: { md: 'none' } }}
        >
          <MenuIcon />
        </IconButton>

        <Box
          sx={{
            borderRadius: 2,
            backgroundColor: alpha(theme.palette.common.black, 0.05),
            '&:hover': { backgroundColor: alpha(theme.palette.common.black, 0.08) },
            p: '4px 8px',
            display: 'flex',
            alignItems: 'center',
            width: { xs: '100%', sm: 300 },
            maxWidth: { xs: '100%', sm: 300 },
          }}
        >
          <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
          <InputBase
            placeholder="검색..."
            sx={{ flex: 1, fontSize: '14px' }}
            inputProps={{ 'aria-label': '검색' }}
          />
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Tooltip title="알림">
            <IconButton
              size="large"
              color="inherit"
              onClick={handleNotificationsOpen}
              sx={{ mr: 1 }}
            >
              {/* 기존 더미 배지: <Badge badgeContent={3} color="error"> */}
              <Badge badgeContent={unreadCount} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
          </Tooltip>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              borderRadius: 2,
              p: '6px 8px',
              '&:hover': { bgcolor: alpha(theme.palette.common.black, 0.05) },
            }}
            onClick={handleProfileMenuOpen}
          >
            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                mr: 1,
              }}
            >
              {userData?.name?.charAt(0) || userData?.contactEmail?.charAt(0) || 'H'}
            </Avatar>
            <Box sx={{ display: { xs: 'none', sm: 'flex' }, flexDirection: 'column' }}>
              <Typography variant="subtitle2" noWrap>
                {userData?.name || userData?.contactEmail || '병원 관리자'}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                관리자
              </Typography>
            </Box>
          </Box>
        </Box>
      </Toolbar>

      {/* 프로필 메뉴 */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        keepMounted
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          elevation: 2,
          sx: { 
            minWidth: 200, 
            mt: 1.5, 
            borderRadius: 2,
            overflow: 'visible',
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          }
        }}
      >
        <Box sx={{ py: 1, px: 2 }}>
          <Typography variant="subtitle2">
            {userData?.name || userData?.contactEmail || '병원 관리자'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {userData?.contactEmail || ''}
          </Typography>
        </Box>
        <Divider />
        <MenuItem onClick={handleProfileClick}>
          <PersonIcon fontSize="small" sx={{ mr: 1 }} />
          내 프로필
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <SettingsIcon fontSize="small" sx={{ mr: 1 }} />
          설정
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <HelpIcon fontSize="small" sx={{ mr: 1 }} />
          도움말
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout} sx={{ color: theme.palette.error.main }}>
          <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
          로그아웃
        </MenuItem>
      </Menu>

      {/* 실제 데이터 연동된 알림 메뉴 */}
      <Menu
        anchorEl={notificationsAnchor}
        open={Boolean(notificationsAnchor)}
        onClose={handleNotificationsClose}
        keepMounted
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          elevation: 2,
          sx: { 
            minWidth: 320, 
            maxWidth: 380,
            maxHeight: 500, 
            mt: 1.5, 
            borderRadius: 2 
          }
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1">
            알림 {unreadCount > 0 && <Chip size="small" label={unreadCount} color="error" sx={{ ml: 1 }} />}
          </Typography>
          {unreadCount > 0 && (
            <Button size="small" color="primary" onClick={handleMarkAllAsRead}>
              모두 읽음 표시
            </Button>
          )}
        </Box>
        <Divider />
        
        {isLoading ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              알림을 불러오는 중...
            </Typography>
          </Box>
        ) : error ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="error">
              알림을 불러올 수 없습니다.
            </Typography>
          </Box>
        ) : recentNotifications.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              새로운 알림이 없습니다.
            </Typography>
          </Box>
        ) : (
          recentNotifications.map((notification, index) => (
            <React.Fragment key={notification.id}>
              <MenuItem 
                onClick={() => handleNotificationClick(notification)}
                sx={{ 
                  py: 1.5,
                  px: 2,
                  bgcolor: notification.isRead ? 'transparent' : alpha(theme.palette.primary.main, 0.08),
                  '&:hover': {
                    bgcolor: notification.isRead 
                      ? alpha(theme.palette.common.black, 0.04)
                      : alpha(theme.palette.primary.main, 0.12)
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
                  {getNotificationIcon(notification.type as unknown as HospitalNotificationType)}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: notification.isRead ? 400 : 600,
                        mb: 0.5,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {notification.title}
                    </Typography>
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        lineHeight: 1.3,
                      }}
                    >
                      {notification.message}
                    </Typography>
                    <Typography 
                      variant="caption" 
                      display="block" 
                      color="text.secondary" 
                      sx={{ mt: 0.5, fontSize: '0.7rem' }}
                    >
                      {formatTimeAgo(notification.createdAt)}
                    </Typography>
                  </Box>
                  {!notification.isRead && (
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: 'primary.main',
                        ml: 1,
                        mt: 0.5,
                        flexShrink: 0
                      }}
                    />
                  )}
                </Box>
              </MenuItem>
              {index < recentNotifications.length - 1 && <Divider />}
            </React.Fragment>
          ))
        )}
        
        {recentNotifications.length > 0 && (
          <>
            <Divider />
            <Box sx={{ p: 1.5, textAlign: 'center' }}>
              <Button size="small" onClick={handleViewAllNotifications}>
                모든 알림 보기
              </Button>
            </Box>
          </>
        )}
      </Menu>

      {/* 
        === 백업된 기존 더미 알림 메뉴 ===
      <Menu
        anchorEl={notificationsAnchor}
        open={Boolean(notificationsAnchor)}
        onClose={handleNotificationsClose}
        keepMounted
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          elevation: 2,
          sx: { 
            minWidth: 300, 
            maxWidth: 320,
            maxHeight: 400, 
            mt: 1.5, 
            borderRadius: 2 
          }
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1">알림</Typography>
          <Button size="small" color="primary">모두 읽음 표시</Button>
        </Box>
        <Divider />
        <MenuItem onClick={handleNotificationsClose}>
          <Box sx={{ py: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              새 지원자 - 김간호
            </Typography>
            <Typography variant="caption" color="text.secondary">
              내과 간호사 포지션에 새로운 지원서가 도착했습니다.
            </Typography>
            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
              10분 전
            </Typography>
          </Box>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleNotificationsClose}>
          <Box sx={{ py: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              면접 알림 - 오늘 15:00
            </Typography>
            <Typography variant="caption" color="text.secondary">
              이간호님과의 면접이 곧 시작됩니다.
            </Typography>
            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
              1시간 전
            </Typography>
          </Box>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleNotificationsClose}>
          <Box sx={{ py: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              시스템 알림
            </Typography>
            <Typography variant="caption" color="text.secondary">
              시프트 오퍼가 자동으로 마감되었습니다.
            </Typography>
            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
              어제
            </Typography>
          </Box>
        </MenuItem>
        <Divider />
        <Box sx={{ p: 1.5, textAlign: 'center' }}>
          <Button size="small" onClick={handleNotificationsClose}>
            모든 알림 보기
          </Button>
        </Box>
      </Menu>
      */}
    </AppBar>
  );
};

export default TopBar; 